import React, { useState, useMemo } from 'react';
import { useLanguage } from '@/hooks/use-language';
import { useEvents } from '@/hooks/use-events';
import { useSupabase } from '@/contexts/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar, Trash2, StopCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import type { EventData } from '@/contexts/event/EventContext';

interface RecurringEventManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seriesId: string;
  onSeriesUpdated: () => void;
}

export function RecurringEventManager({ 
  open, 
  onOpenChange, 
  seriesId, 
  onSeriesUpdated 
}: RecurringEventManagerProps) {
  const { t } = useLanguage();
  const { events } = useEvents();
  const supabase = useSupabase();
  const [loading, setLoading] = useState(false);

  // Get all events in the series
  const seriesEvents = useMemo(() => {
    return events
      .filter(e => e.parentEventId === seriesId || e.id === seriesId)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [events, seriesId]);

  const masterEvent = seriesEvents.find(e => e.id === seriesId);

  const handleStopRecurrence = async () => {
    if (!masterEvent) return;
    if (!confirm('Stop recurring series? Future events will not be generated.')) return;

    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const { error } = await supabase.functions.invoke('manage-event', {
        body: {
          action: 'stop_recurrence',
          parent_id: seriesId,
          end_date: today,
        },
      });

      if (error) throw error;

      toast.success('Recurrence stopped');
      onSeriesUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error stopping recurrence:', error);
      toast.error('Failed to stop recurrence');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFutureSeries = async () => {
    if (!confirm('Delete all future events in this series? This cannot be undone.')) return;

    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase.functions.invoke('manage-event', {
        body: {
          action: 'delete_future_series',
          parent_id: seriesId,
          from_date: today,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error('Failed to delete future events');

      toast.success('Future events deleted');
      onSeriesUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error deleting future events:', error);
      toast.error('Failed to delete events');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllSeries = async () => {
    if (!confirm('Delete ENTIRE series? This will remove all events and cannot be undone.')) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-event', {
        body: {
          action: 'delete_entire_series',
          parent_id: seriesId,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error('Failed to delete series');

      toast.success('Series deleted');
      onSeriesUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error deleting series:', error);
      toast.error('Failed to delete series');
    } finally {
      setLoading(false);
    }
  };

  if (!masterEvent) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t('admin.view_series')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Series Info */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div>
                  <span className="font-semibold">Title:</span>{' '}
                  {masterEvent.titleKey ? t(masterEvent.titleKey) : masterEvent.title}
                </div>
                <div>
                  <span className="font-semibold">Pattern:</span>{' '}
                  <Badge>{masterEvent.recurrencePattern}</Badge>
                </div>
                <div>
                  <span className="font-semibold">Total Events:</span>{' '}
                  {seriesEvents.length}
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Event List */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            <h4 className="font-semibold text-sm">Events in Series</h4>
            {seriesEvents.map((event) => {
              const isPast = new Date(event.date) < new Date();
              return (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <div className="font-medium">
                      {format(new Date(event.date), 'PPP')} at {event.time}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {event.participants.length}/{event.maxParticipants} participants
                    </div>
                  </div>
                  <Badge variant={isPast ? 'secondary' : 'default'}>
                    {isPast ? 'Past' : 'Upcoming'}
                  </Badge>
                </div>
              );
            })}
          </div>

          <Separator />

          {/* Actions */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm">Series Actions</h4>
            
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleStopRecurrence}
              disabled={loading}
            >
              <StopCircle className="h-4 w-4 mr-2" />
              Stop Recurrence (Keep Existing Events)
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start text-orange-600 hover:text-orange-700"
              onClick={handleDeleteFutureSeries}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete All Future Events
            </Button>

            <Button
              variant="destructive"
              className="w-full justify-start"
              onClick={handleDeleteAllSeries}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Entire Series
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
